import { defer, map, merge, type Observable, of } from 'rxjs'
import type { KeyPress } from '@/utils/keyboard-util'
import type { Member } from '@/features/iam/members/Member'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type {
  FindCurrentWorkspaceFn,
  GetCurrentMemberFn,
  GetCurrentUserFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetCurrentWorkspaceFn,
  GetEnterpriseConnectionFn,
  GetMultiFactorAuthEnrollmentUrlFn,
  HasMultipleWorkspacesFn,
  IsEnrolledInMultiFactorAuthFn,
  IsWorkspaceSelectorOpenFn,
  OpenWorkspaceSearchFn,
  GetTeamMembersForWorkspaceMemberFn,
  GetUserDataFn,
  ReadServerRenderedWorkspaceFn,
  ToggleWorkspaceSelectorFn,
} from '@/features/iam'
import type { PromptCreateJobFn } from '@/features/jobs'
import type { CanViewPayoutsAdminFn } from '@/features/payouts'
import type { CanViewLegendsFn } from '@/features/legends'
import type { ToggleNotificationsSidebarFn } from '@/features/notifications'
import {
  AlertsManager,
  createAlertErrorHandlerFactory,
  createAlertRetryWrapperFactory,
} from '@/features/shell/alerts/AlertsManager'
import type {
  Alert,
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
  AlertState,
} from '@/features/shell/alerts/Alert'
import { Shortcuts } from '@/features/shell/shortcuts/Shortcuts'
import { ThemeManager } from '@/features/shell/theming/ThemeManager'
import { ShellInfo } from '@/features/shell/ShellInfo'
import { NativePlatformDelegate } from '@/features/shell/platform/NativePlatformDelegate'
import { DefaultPlatformDelegate } from '@/features/shell/platform/DefaultPlatformDelegate'
import { GlobalNavigationViewModel } from '@/features/shell/global-navigation/GlobalNavigationViewModel'
import { filterNonNil } from '@/utils/rx-util'
import { byDesign } from '@/infra/design'
import logoWorklayerBlueUrl from '@/assets/misc/logo_worklayer-blue.svg'
import imgLoginUrl from '@/assets/misc/img_login.svg'
import imgLoginRefreshUrl from '@/assets/misc/img_login-refresh.svg'

export type {
  Alert,
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
  AlertState,
}

/**
 * Options for {@link ShellFeature}.
 */
export interface ShellFeatureOptions {
  /**
   * Stream of global key presses.
   */
  keyboard$: Observable<KeyPress>

  /**
   * Delegate for getting the current workspace.
   */
  getCurrentWorkspace: GetCurrentWorkspaceFn

  /**
   * Delegate for getting the current workspace, or `null` when none is
   * selected.
   */
  findCurrentWorkspace: FindCurrentWorkspaceFn

  /**
   * Delegate for getting the current member.
   */
  getCurrentMember: GetCurrentMemberFn

  /**
   * Delegate for getting the current user.
   */
  getCurrentUser: GetCurrentUserFn

  /**
   * Delegate for getting the current user's workspace memberships.
   */
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn

  /**
   * Delegate for getting the current user's team members in a workspace.
   */
  getTeamMembersForWorkspaceMember: GetTeamMembersForWorkspaceMemberFn

  /**
   * Delegate for getting a user's data.
   */
  getUserData: GetUserDataFn

  /**
   * Stream of the current member, used for theming.
   */
  currentMember$: Observable<Member>

  /**
   * Reads the workspace of the custom domain the app is served from, which
   * brands the app before a workspace is selected.
   */
  readServerRenderedWorkspace: ReadServerRenderedWorkspaceFn

  /**
   * Whether the user belongs to more than one workspace.
   */
  hasMultipleWorkspaces: HasMultipleWorkspacesFn

  /**
   * Whether the workspace selector is open.
   */
  isWorkspaceSelectorOpen: IsWorkspaceSelectorOpenFn

  /**
   * Opens or closes the workspace selector.
   */
  toggleWorkspaceSelector: ToggleWorkspaceSelectorFn

  /**
   * Opens the search for joining another workspace.
   */
  openWorkspaceSearch: OpenWorkspaceSearchFn

  /**
   * Whether the user is enrolled in multi-factor authentication.
   */
  isEnrolledInMultiFactorAuth: IsEnrolledInMultiFactorAuthFn

  /**
   * Delegate for getting the multi-factor authentication enrollment URL.
   */
  getMultiFactorAuthEnrollmentUrl: GetMultiFactorAuthEnrollmentUrlFn

  /**
   * Delegate for getting the user's enterprise connection, used for going to HQ.
   */
  getEnterpriseConnection: GetEnterpriseConnectionFn

  /**
   * Prompts the user to create a job.
   */
  promptCreateJob: PromptCreateJobFn

  /**
   * Stream of the unread messages count.
   */
  unreadMessagesCount$: Observable<number>

  /**
   * Stream of whether the current member can view opportunities.
   */
  canViewOpportunities$: Observable<boolean>

  /**
   * Stream of the available opportunities count.
   */
  opportunityCount$: Observable<number>

  /**
   * Stream of whether the current member can view their payouts.
   */
  canViewProPayouts$: Observable<boolean>

  /**
   * Whether the current member can view the payouts admin.
   */
  canViewPayoutsAdmin: CanViewPayoutsAdminFn

  /**
   * Whether the user may see the legends of any of their workspaces.
   */
  canViewLegends: CanViewLegendsFn

  /**
   * Stream of the unread notifications count.
   */
  unreadNotificationsCount$: Observable<number>

  /**
   * Opens or closes the notifications sidebar.
   */
  toggleNotificationsSidebar: ToggleNotificationsSidebarFn
}

/**
 * Manages the surrounding application shell such as the navigation and alerts.
 */
export type ShellFeature = ReturnType<typeof ShellFeature>

export function ShellFeature(options: ShellFeatureOptions) {
  const platformDelegate = NativePlatformDelegate.isSupported
    ? new NativePlatformDelegate()
    : new DefaultPlatformDelegate()

  const alertsManager = new AlertsManager()
  const newAlert: AlertFactory = () => alertsManager.newAlert()
  const createAlertErrorHandler = createAlertErrorHandlerFactory(alertsManager)
  const createAlertRetryWrapper = createAlertRetryWrapperFactory(alertsManager)

  const shortcuts = new Shortcuts(options.keyboard$)

  const globalNavigationVm = new GlobalNavigationViewModel(
    options.getCurrentWorkspace,
    options.getCurrentMember,
    options.getCurrentUser,
    options.getCurrentUserWorkspaceMemberships,
    options.getTeamMembersForWorkspaceMember,
    options.getUserData,
    options.hasMultipleWorkspaces,
    options.isWorkspaceSelectorOpen,
    options.toggleWorkspaceSelector,
    options.openWorkspaceSearch,
    options.isEnrolledInMultiFactorAuth,
    options.getMultiFactorAuthEnrollmentUrl,
    options.getEnterpriseConnection,
    options.promptCreateJob,
    options.canViewPayoutsAdmin,
    options.canViewLegends,
    options.toggleNotificationsSidebar,
    createAlertErrorHandler,
    options.unreadMessagesCount$,
    options.canViewOpportunities$,
    options.opportunityCount$,
    options.canViewProPayouts$,
    options.unreadNotificationsCount$,
  )

  const themeManager = new ThemeManager(
    merge(
      defer(() => of(options.readServerRenderedWorkspace())).pipe(
        filterNonNil(),
      ),
      options.currentMember$.pipe(map((member) => member.workspace)),
    ),
  )
  themeManager.activate()

  const shellInfo = ShellInfo(() => {
    const workspace =
      options.findCurrentWorkspace() ??
      options.readServerRenderedWorkspace() ??
      Workspace.none
    const { logos } = workspace.branding
    return {
      name: workspace.name || 'Worklayer',
      iconUrl:
        logos.icon ||
        'https://storage.googleapis.com/taxfyle-assets-public/taxfyle-workspace-assets/taxfyle-icon-blue.png',
      invertedIconUrl:
        logos.iconWhite ||
        'https://storage.googleapis.com/worklayer-assets/default-workspace-branding/icon-white.svg',
      logoUrl: logos.primary || logoWorklayerBlueUrl,
      landingPageImageUrl:
        workspace.branding.landingPage.image ||
        byDesign(imgLoginUrl, imgLoginRefreshUrl),
      corporateMode: workspace.branding.corporateMode,
    }
  })

  return {
    // Internal
    internal: {
      // Not lazy-loaded because the shell is used everywhere.
      alertsManager,
      globalNavigationVm,
      themeManager,
    },

    // Public
    shellInfo,
    newAlert,
    createAlertErrorHandler,
    createAlertRetryWrapper,
    getExternalUrlDelegate: platformDelegate.getExternalUrlDelegate,
    getAddToCalendarDelegate: platformDelegate.getAddToCalendarDelegate,
    getDocumentDownloadDelegate: platformDelegate.getDocumentDownloadDelegate,
    getAuthTokenDelegate: platformDelegate.getAuthTokenDelegate,
    renameShortcut$: shortcuts.rename$,
  }
}
